<template>
  <div class="mb-3">
    <div class="table-responsive">
      <table class="table table-striped table-hover align-middle mb-0">
        <thead>
          <tr>
            <th>Getnet</th>
            <th>Venda</th>
            <th>Venc.</th>
            <th>Autorização</th>
            <th>Parcela</th>
            <th class="text-end">Bruto</th>
            <th class="text-end">Líquido</th>
            <th class="text-end">Dif.</th>
            <th>Status</th>
            <th>Vínculo</th>
            <th class="text-end">Ações</th>
          </tr>
        </thead>

        <tbody>
          <tr v-if="!items.length">
            <td colspan="11" class="text-center text-muted py-4">Nenhum item encontrado.</td>
          </tr>

          <tr v-for="item in items" :key="item.id">
            <td>
              <span
                class="identificador-item"
                :title="`Item Getnet #${item.id}`"
              >
                #{{ item.id }}
              </span>
            </td>
            <td>{{ formatDate(item.data_venda) }}</td>
            <td>{{ formatDate(item.data_vencimento) }}</td>
            <td>{{ item.autorizacao || '—' }}</td>
            <td>{{ parcelaLabel(item) }}</td>
            <td class="text-end">{{ formatCurrency(item.valor_parcela ?? item.valor_bruto) }}</td>
            <td class="text-end">{{ formatCurrency(item.valor_liquido) }}</td>
            <td class="text-end">
              <span :class="differenceClass(item.diferenca_bruto_liquido)">
                {{ formatCurrency(item.diferenca_bruto_liquido) }}
              </span>
            </td>
            <td>
              <span class="status-badge" :class="statusClass(item)">
                {{ statusLabel(item) }}
              </span>

              <small
                v-if="item.motivo_pendencia"
                class="pendencia-motivo d-block mt-1"
              >
                {{ item.motivo_pendencia }}
              </small>
            </td>
            <td>{{ formatarVinculoResumo(item.movimento_label_amigavel) }}</td>

            <td class="table-action-cell">
              <template v-if="podeRegularizarVendaCancelada(item)">
                <i
                  class="bi bi-cash-coin text-primary mx-1 action-icon"
                  title="Regularizar venda cancelada liquidada"
                  @click="$emit('regularizar-venda-cancelada', { origemTipo: 'OPERADORA', item })"
                ></i>
              </template>

              <template v-else-if="podeConciliarManualmente(item)">
                <i
                  class="bi bi-link-45deg text-primary mx-1 action-icon"
                  title="Conciliar"
                  @click="$emit('conciliar', { origemTipo: 'OPERADORA', item })"
                ></i>
              </template>

              <template v-else-if="item.status_conciliacao === 'CONCILIADO_AUTOMATICO' || item.status_conciliacao === 'CONCILIADO_MANUAL'">
                <i
                  class="bi bi-eye-fill text-primary mx-1 action-icon"
                  title="Detalhar"
                  @click="$emit('detalhar', { origemTipo: 'OPERADORA', item })"
                ></i>

                <i
                  class="bi bi-arrow-counterclockwise text-danger mx-1 action-icon"
                  title="Desfazer"
                  @click="$emit('desfazer', { origemTipo: 'OPERADORA', item })"
                ></i>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="pagination" class="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ pagination.total || 0 }} registros
      </div>

      <div class="d-flex align-items-center" style="gap: .5rem;">
        <select
          class="form-select form-select-sm"
          style="width: 6rem"
          :value="pagination.limit || 10"
          @change="emit('change-limit', Number($event.target.value))"
        >
          <option v-for="n in pageOptions" :key="n" :value="n">
            {{ n }} / página
          </option>
        </select>
      </div>

      <div class="d-flex align-items-center">
        <BPagination
          :model-value="pagination.page || 1"
          :per-page="pagination.limit || 10"
          :total-rows="pagination.total || 0"
          size="sm"
          limit="5"
          first-text="«"
          prev-text="‹"
          next-text="›"
          last-text="»"
          @update:model-value="emit('change-page', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { BPagination } from 'bootstrap-vue-next'

const props = defineProps({
  items: { type: Array, default: () => [] },
  pagination: { type: Object, default: () => ({}) },
  totalLabel: { type: String, default: '' }
})

const emit = defineEmits([
  'conciliar',
  'regularizar-venda-cancelada',
  'desfazer',
  'detalhar',
  'change-page',
  'change-limit'
])

const pageOptions = [10, 15, 20, 25, 50]

const startRow = computed(() => {
  const total = Number(props.pagination?.total || 0)
  if (!total) return 0
  const page = Number(props.pagination?.page || 1)
  const limit = Number(props.pagination?.limit || 10)
  return (page - 1) * limit + 1
})

const endRow = computed(() => {
  const total = Number(props.pagination?.total || 0)
  if (!total) return 0
  const page = Number(props.pagination?.page || 1)
  const limit = Number(props.pagination?.limit || 10)
  return Math.min(page * limit, total)
})

function formatDate(v) {
  if (!v) return '—'
  const [y, m, d] = String(v).slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

function formatCurrency(v) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v || 0))
}

function normalizeStatus(v) {
  const map = {
    PENDENTE: 'Pendente de revisão',
    CONCILIADO_AUTOMATICO: 'Conciliado automático',
    CONCILIADO_MANUAL: 'Conciliado manual',
    PENDENTE_AJUSTE: 'Pendente ajuste',
    IGNORADO: 'Ignorado',
    DIVERGENTE: 'Divergente',

    AGUARDANDO_CONCILIACAO_BANCO_OPERADORA: 'Aguardando conciliação Banco x Operadora',
    PAGAMENTO_CONSOLIDADO: 'Pagamento consolidado',
    DESPESA_BANCARIA: 'Despesa bancária',
    PENDENTE_SISTEMA: 'Pendente de revisão',

    CONCILIADO_BANCO_OPERADORA_AUTOMATICO: 'Conciliado automático',
    CONCILIADO_BANCO_OPERADORA_MANUAL: 'Conciliado manual',

    PIX_RECEBIDO: 'Pix recebido',
    PIX_ENVIADO: 'Pix enviado',
    CARTAO_GETNET_CREDITO: 'Getnet crédito',
    CARTAO_GETNET_DEBITO: 'Getnet débito',
    CHEQUE_DEPOSITO: 'Cheque depósito',
    CHEQUE_DEVOLVIDO: 'Cheque devolvido',
    BOLETO: 'Boleto',
    DARF: 'DARF',
    APLICACAO: 'Aplicação',
    RESGATE_APLICACAO: 'Resgate aplicação',
    SALARIOS: 'Salários',
    TARIFA_BANCARIA: 'Tarifa bancária',
    TRANSFERENCIA_BANCARIA: 'Transferência bancária',
    TRANSFERENCIA_INTERNA: 'Transferência interna',
    DEBITO_AUTOMATICO: 'Débito automático',
    OUTRO: 'Outro',

    SEM_LANCAMENTO_SISTEMA: 'Sem lançamento no sistema',
    LANCAMENTO_CANCELADO_SISTEMA: 'Lançamento cancelado no sistema',
    LANCAMENTO_CANCELADO_TRATADO: 'Cancelamento tratado',
    LANCAMENTO_CANCELADO_LIQUIDADO: 'Venda cancelada liquidada',
    AUTORIZACAO_DIVERGENTE: 'Autorização divergente',
    POSSIVEL_LANCAMENTO_CONSOLIDADO: 'Possível lançamento consolidado',
    POSSIVEL_LANCAMENTO_AGRUPADO: 'Possível lançamento agrupado',
    AMBIGUO: 'Ambíguo'
  }

  const key = String(v || '').toUpperCase()
  return map[key] || String(v || '').replaceAll('_', ' ')
}

function formatarVinculoResumo(texto) {
  const valor = String(texto || '').trim()
  if (!valor) return '—'

  const partes = valor.split(' + ').map(p => p.trim()).filter(Boolean)
  if (partes.length <= 1) return valor

  const restantes = partes.length - 1
  const sufixo = restantes === 1 ? 'vínculo' : 'vínculos'

  return `${partes[0]} + ${restantes} ${sufixo}`
}

function parcelaLabel(item) {
  if (!item.parcela_numero) return '—'
  return `${item.parcela_numero}/${item.parcela_total || item.parcela_numero}`
}

function podeRegularizarVendaCancelada(item) {
  const status = String(
    item?.status_conciliacao || ''
  ).toUpperCase()

  const pendencia = String(
    item?.tipo_pendencia || ''
  ).toUpperCase()

  return (
    status === 'PENDENTE' &&
    pendencia === 'LANCAMENTO_CANCELADO_LIQUIDADO'
  )
}

function podeConciliarManualmente(item) {
  const status = String(
    item?.status_conciliacao || ''
  ).toUpperCase()

  const pendencia = String(
    item?.tipo_pendencia || ''
  ).toUpperCase()

  if (
    [
      'CONCILIADO_AUTOMATICO',
      'CONCILIADO_MANUAL'
    ].includes(status)
  ) {
    return false
  }

  /*
   * Um lançamento removido pelo cancelamento da matrícula
   * nunca deve ser conciliado em Operadora x Sistema.
   *
   * Se o ajuste ainda estiver pendente, a ação correta ocorre
   * em Ajustes da Operadora.
   *
   * Se o ajuste já tiver sido tratado, não há mais nenhuma
   * ação operacional a executar neste item.
   */
  if (
    [
      'LANCAMENTO_CANCELADO_SISTEMA',
      'LANCAMENTO_CANCELADO_TRATADO',
      'LANCAMENTO_CANCELADO_LIQUIDADO'
    ].includes(pendencia)
  ) {
    return false
  }

  return true
}

function statusLabel(item) {
  const status = String(item?.status_conciliacao || '').toUpperCase()

  if (status.includes('CONCILIADO')) {
    return normalizeStatus(status)
  }

  if (status === 'PENDENTE' && item?.tipo_pendencia) {
    return normalizeStatus(item.tipo_pendencia)
  }

  return normalizeStatus(status || 'PENDENTE')
}

function statusClass(item) {
  const status = String(item?.status_conciliacao || '').toUpperCase()
  const pendencia = String(item?.tipo_pendencia || '').toUpperCase()

  if (status.includes('AUTOMATICO')) return 'is-green'
  if (status.includes('MANUAL')) return 'is-blue'
  if (status.includes('AJUSTE')) return 'is-amber'
  if (status.includes('IGNORADO')) return 'is-gray'

  if (pendencia === 'AUTORIZACAO_DIVERGENTE') return 'is-purple'
  if (
    [
      'POSSIVEL_LANCAMENTO_CONSOLIDADO',
      'POSSIVEL_LANCAMENTO_AGRUPADO'
    ].includes(pendencia)
  ) return 'is-amber'
  if (pendencia === 'AMBIGUO') return 'is-amber'
  if (pendencia === 'LANCAMENTO_CANCELADO_TRATADO') return 'is-green'
  if (pendencia === 'LANCAMENTO_CANCELADO_LIQUIDADO') return 'is-amber'
  if (pendencia === 'LANCAMENTO_CANCELADO_SISTEMA') return 'is-gray'
  if (pendencia === 'SEM_LANCAMENTO_SISTEMA') return 'is-yellow'

  return 'is-yellow'
}

function differenceClass(v) {
  const n = Math.abs(Number(v || 0))
  if (n === 0) return 'diff-zero'
  if (n > 0 && n <= 50) return 'diff-fee'
  return 'diff-alert'
}
</script>

<style scoped>
.table {
  margin-bottom: 0;
}

.table th {
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
  vertical-align: middle;
}

.table td {
  font-size: 0.83rem;
  vertical-align: middle;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.status-badge {
  display: inline-block;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  text-transform: none;
}

.table-action-cell {
  text-align: right !important;
  vertical-align: middle !important;
  white-space: nowrap;
}

.action-icon {
  cursor: pointer;
  font-size: 0.95rem;
  transition: opacity 0.15s ease;
}

.action-icon:hover {
  opacity: 0.75;
}

.pendencia-motivo {
  max-width: 260px;
  color: #6b7280;
  font-size: 0.72rem;
  line-height: 1.25;
}

.identificador-item {
  display: inline-block;
  min-width: 52px;
  padding: 3px 8px;
  border-radius: 6px;
  background: #e2e8f0;
  color: #334155;
  font-size: 0.76rem;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  white-space: nowrap;
}

:deep(.pagination.pagination-sm .page-link) {
  font-size: 0.8rem;
  line-height: 1;
  border-radius: 0 !important;
}

:deep(.pagination.pagination-sm) {
  margin-bottom: 0;
}

:deep(.pagination.pagination-sm .page-item.active .page-link) {
  z-index: 1;
}

.is-green { background: #dcfce7; color: #166534; }
.is-blue { background: #dbeafe; color: #1d4ed8; }
.is-yellow { background: #fef3c7; color: #92400e; }
.is-amber { background: #fde68a; color: #92400e; }
.is-gray { background: #e5e7eb; color: #374151; }
.is-purple { background: #ede9fe; color: #6d28d9; }

.diff-zero { color: #111827; }
.diff-fee { color: #b45309; font-weight: 600; }
.diff-alert { color: #b91c1c; font-weight: 700; }
</style>